import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Lock, Shield, Save } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsManager() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders } = useAuth();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        getAuthHeaders()
      );
      toast.success("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-manager">
      <div className="mb-8">
        <h1 className="font-heading text-3xl text-white mb-2">Settings</h1>
        <p className="text-white/60">Manage your account security</p>
      </div>

      <div className="max-w-lg">
        <div className="glass-card p-6">
          <h2 className="font-heading text-xl text-white flex items-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-gold" />
            Change Password
          </h2>

          <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
            <div>
              <Label className="text-white/80">Current Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="bg-casino-secondary border-white/10 text-white pl-10"
                  placeholder="Enter current password"
                  required
                  data-testid="current-password-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/80">New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-casino-secondary border-white/10 text-white pl-10"
                  placeholder="Min 8 characters"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/80">Confirm New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-casino-secondary border-white/10 text-white pl-10"
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-gold text-black hover:bg-gold-dark mt-2"
              data-testid="save-password-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
